import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Lock, Eye, EyeOff, Check, Home } from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const T = { fontFamily: "'Inter Tight', sans-serif" };
  const S = { fontFamily: "'IBM Plex Sans', sans-serif" };
  const L = { fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", fontWeight: "600", letterSpacing: "0.16em", color: "#475569", textTransform: "uppercase", marginBottom: "6px", display: "block" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed — link may have expired");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative" style={{ background: "#0B0D10" }} data-testid="reset-password-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 ace-bg-radial" />
        <div className="ace-noise" />
      </div>

      <Link to="/" data-testid="home-button" className="fixed top-6 left-6 z-50 flex items-center gap-2 px-3 py-2 transition-all hover:bg-white/[0.03]"
        style={{ clipPath: "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <Home size={14} style={{ color: "#94A3B8" }} />
        <span style={{ ...T, fontSize: "10px", fontWeight: "600", color: "#94A3B8", letterSpacing: "0.08em" }}>HOME</span>
      </Link>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="relative w-full max-w-sm mx-4">
        <div className="octa-clip" style={{ background: "rgba(255,255,255,0.045)", padding: "1px" }}>
          <div className="octa-clip p-8" style={{ background: "linear-gradient(180deg, rgba(22,25,32,1) 0%, rgba(15,17,21,1) 100%)" }}>
            <div className="absolute top-0 left-0 right-0" style={{ height: "1px", background: "linear-gradient(90deg, transparent 10%, rgba(255,255,255,0.06) 50%, transparent 90%)" }} />

            <div className="flex items-center gap-3 mb-8">
              <div className="w-9 h-9 octa-clip-sm bg-[#2563EB] flex items-center justify-center">
                <Zap size={18} className="text-white" strokeWidth={2} />
              </div>
              <div>
                <h1 style={{ ...T, fontWeight: "700", color: "#fff", fontSize: "16px", letterSpacing: "0.15em", textTransform: "uppercase" }}>ACE OS</h1>
                <p style={{ ...T, color: "#475569", fontSize: "10px", letterSpacing: "0.1em", fontWeight: "500" }}>Trading Command System</p>
              </div>
            </div>

            {!done ? (
              <>
                <h2 style={{ ...T, fontWeight: "600", color: "#E2E8F0", fontSize: "18px", marginBottom: "4px" }}>New Password</h2>
                <p style={{ ...S, color: "#475569", fontSize: "13px", marginBottom: "24px" }}>Enter your new password below</p>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                  <div>
                    <label style={L}>New Password</label>
                    <div className="relative">
                      <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
                      <input type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="New password" required data-testid="new-password-input"
                        className="w-full bg-white/[0.03] border border-white/[0.06] text-[#E2E8F0] text-sm pl-9 pr-10 py-2.5 placeholder-[#334155] focus:border-[#2563EB]/40 focus:outline-none transition-all"
                        style={S} />
                      <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#94A3B8]">
                        {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div data-testid="reset-error" className="octa-clip-sm text-xs p-3" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", color: "#EF4444", ...S }}>
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                    className="w-full octa-clip-sm text-white py-3 transition-all duration-150 disabled:opacity-50"
                    style={{ background: "#2563EB", ...T, fontWeight: "600", fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                    {loading ? "Updating..." : "Set New Password"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-4" data-testid="reset-success">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.15)" }}>
                  <Check size={20} style={{ color: "#22C55E" }} />
                </div>
                <h2 style={{ ...T, fontWeight: "600", color: "#E2E8F0", fontSize: "16px", marginBottom: "6px" }}>Password Updated</h2>
                <p style={{ ...S, color: "#64748B", fontSize: "13px", marginBottom: "20px" }}>You can now sign in with your new password.</p>
                <button onClick={() => navigate("/login")} data-testid="go-to-login"
                  className="octa-clip-sm text-white px-6 py-2.5 transition-all"
                  style={{ background: "#2563EB", ...T, fontWeight: "600", fontSize: "11px", letterSpacing: "0.1em" }}>
                  SIGN IN
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
